/**
 * Helpers purs pour détecter et traiter les réponses HTTP 429
 * "Too Many Requests" émises par le backend ( ex : quota dépôts
 * simultanés par utilisateur sur l'endpoint d'ingestion CSV ) .
 *
 * <p><b>Pourquoi</b> : sans handling explicite , un POST 429 finit
 * uniquement loggé en console ( cf {@code Fetcher} ) , l'utilisateur
 * clique "déposer" et ne voit RIEN se passer , impression de bug
 * silencieux . Solution : intercepter 429 au niveau global Fetcher
 * et émettre un toast clair via l'injection {@link RateLimitNotifier} .
 *
 * <p><b>SOLID</b> :
 * <ul>
 *   <li><b>SRP</b> : ce module ne fait QUE détection + extraction ,
 *       l'affichage est délégué à {@link RateLimitNotifier} ;</li>
 *   <li><b>OCP</b> : ajouter une nouvelle source d'info ( body JSON ,
 *       header custom ) = nouveau {@code resolveX} sans toucher au
 *       reste ;</li>
 *   <li><b>DIP</b> : Fetcher dépend de l'interface
 *       {@link RateLimitNotifier} , pas de l'implémentation concrète
 *       ( testabilité + remplaçabilité ) .</li>
 * </ul>
 */

/**
 * Marqueur ajouté au corps de rejet d'une promesse Fetcher quand la
 * réponse était 429 . Permet aux call-sites ( ex AlertService ) de
 * sauter leur propre toast d'erreur pour éviter le doublon .
 */
export const RATE_LIMITED_ERROR_FLAG = "rateLimited" as const;

/** Code HTTP "Too Many Requests" ( RFC 6585 ) . */
export const HTTP_TOO_MANY_REQUESTS = 429;

/**
 * Implémenté par les composants UI capables de signaler à
 * l'utilisateur qu'il a atteint un quota de requêtes ( ex toast ,
 * snackbar , modal ) . Découplé de Buefy pour permettre les tests .
 */
export interface RateLimitNotifier {
  /**
   * @param retryAfterSec  délai indicatif avant nouvelle tentative
   *                       ( en secondes ) , ou {@code null} si le
   *                       backend ne l'a pas exposé .
   */
  notifyRateLimited(retryAfterSec: number | null): void;
}

/** Vrai si le status HTTP correspond à un rate-limit ( 429 ) . */
export function isRateLimitedStatus(status: number | null | undefined): boolean {
  return status === HTTP_TOO_MANY_REQUESTS;
}

/**
 * Lit le header {@code Retry-After} ( RFC 7231 §7.1.3 ) qui peut
 * encoder soit un nombre de secondes ( delta-seconds ) , soit une
 * date HTTP absolue . Retourne le nombre de secondes restantes ou
 * {@code null} si absent / illisible .
 *
 * <p>Robustesse : tolère {@code headers} null/undefined ( cas Response
 * partiellement construite dans certains chemins xhr ) , trim les
 * espaces , clamp à 0 si la date est dans le passé .
 */
export function extractRetryAfterSeconds(
  headers: Headers | null | undefined,
): number | null {
  if (!headers || typeof headers.get !== "function") return null;
  const raw = headers.get("Retry-After");
  if (!raw) return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const asNumber = Number(trimmed);
  if (Number.isFinite(asNumber) && asNumber >= 0) {
    return Math.round(asNumber);
  }
  const asDateMs = Date.parse(trimmed);
  if (!Number.isFinite(asDateMs)) return null;
  const deltaMs = asDateMs - Date.now();
  if (deltaMs <= 0) return 0;
  return Math.round(deltaMs / 1000);
}

/**
 * Résout la clé i18n + paramètres à injecter dans le toast , selon
 * que {@code Retry-After} ait été exposé ou non .
 */
export function resolveRateLimitMessage(
  retryAfterSec: number | null,
): { key: string; params: Record<string, number> } {
  if (retryAfterSec != null && retryAfterSec > 0) {
    return {
      key: "rateLimit.tooManyUploadsRetryAfter",
      params: { seconds: retryAfterSec },
    };
  }
  return { key: "rateLimit.tooManyUploads", params: {} };
}
